package top.wcpe.allincore.ui.runtime.reactive

import top.wcpe.allincore.ui.runtime.internal.concurrent.AtomicIntCounter
import top.wcpe.allincore.ui.runtime.internal.concurrent.AtomicLongCounter
import kotlin.concurrent.Volatile
import kotlin.jvm.JvmStatic

/**
 * 反应式系统级指标汇聚点（批 D / D4，数据绑定调试 Level 2 降级）。
 *
 * 运行时 `UIElement` 只持有属性当前值、不保留「是否由响应式绑定驱动」的元信息，故「属性 → 订阅源」
 * 逆向映射在运行时不可得，本期降级为只暴露**系统级**响应式状态。
 *
 * [ReactiveDebug] 的 currentRecomposeCount/currentDepsCount 是 ThreadLocal、仅在追踪上下文内有意义；
 * 故这里另设两个**跨线程可读的累计/存活计数**，供 dev-debug 性能面板在另一线程取最近值展示。
 *
 * 生产零开销守卫：[isEnabled] 默认 false，仅 dev-debug 启用调试浮层时置 true；关闭时 record* 第一条
 * 语句即返回，开销仅一次 volatile 读。并发：Effect 可能跨线程构造/重跑/释放，故用 [AtomicLongCounter] /
 * [AtomicIntCounter]（JVM 端为 j.u.c 原子类）保证原子性，读路径无锁。
 *
 * 仅供调试 / 内省用途，不属于对外稳定 API。
 */
object ReactiveMetricsSink {

    /** 全局启用开关：仅 dev-debug 浮层激活时为 true，生产默认 false。 */
    @Volatile
    private var enabled = false

    /** 累计重组（Effect 执行）次数；单调递增。 */
    private val totalRecompose = AtomicLongCounter(0L)

    /** 当前存活的 Effect 数量（构造 +1 / dispose -1）。 */
    private val liveEffect = AtomicIntCounter(0)

    /** 是否启用埋点；热路径据此短路。 */
    @JvmStatic
    fun isEnabled(): Boolean = enabled

    /**
     * 设置启用开关。由 dev-debug 启用 / 关闭调试浮层时调用；关闭时不清空累计值，保留最后状态便于
     * 面板读取「停采前最后数值」。
     */
    @JvmStatic
    fun setEnabled(value: Boolean) {
        enabled = value
    }

    /** 记录一次重组（Effect 执行）；未启用时直接返回。 */
    @JvmStatic
    fun recordRecompose() {
        if (!enabled) {
            return
        }
        totalRecompose.incrementAndGet()
    }

    /** 记录一个 Effect 进入存活态（构造成功）；未启用时直接返回。 */
    @JvmStatic
    fun recordEffectCreated() {
        if (!enabled) {
            return
        }
        liveEffect.incrementAndGet()
    }

    /** 记录一个 Effect 退出存活态（dispose）；未启用时直接返回。 */
    @JvmStatic
    fun recordEffectDisposed() {
        if (!enabled) {
            return
        }
        liveEffect.decrementAndGet()
    }

    /** 读取累计重组次数。 */
    @JvmStatic
    fun totalRecomposeCount(): Long = totalRecompose.get()

    /** 读取当前存活 Effect 数量（不会小于 0）。 */
    @JvmStatic
    fun liveEffectCount(): Int = maxOf(0, liveEffect.get())

    /** 复位所有计数与开关；仅供测试与显式重置使用。 */
    @JvmStatic
    fun reset() {
        enabled = false
        totalRecompose.set(0L)
        liveEffect.set(0)
    }
}
